import { StringUtils } from '../../../shared/utils/string-utils';
import { DateUtils } from '../../../shared/utils/date-utils';
import { SearchParamsUtils } from '../../checkout-page/utils';
import { NumberUtils } from '../../../shared/utils/number-utils';
import { FlightItinerary, AddedProduct } from '../interfaces';
import { IUpSell } from '../../../api/api-checkout/models/rq-checkout-up-sell';
import {
  Departure,
  PricingDetail,
  Returns,
} from '../../../api/api-checkout/models/rq-checkout-search';

export class AbandonedCartUtils {
  static buildEventData(email: string, itinerary: FlightItinerary, upsell?: IUpSell): unknown {
    const { departure, returns, detailPricing } = itinerary.flight;
    const url = this.redirectionUrlBuilder(itinerary, email);

    const formatCity = (city: { name: string }) => StringUtils.capitalizeWords(city.name);
    const formatAirport = (city: { airport: string; code: string }) =>
      `${city.airport} (${city.code})`;

    const buildDeparturesReturn = (item: Departure | Returns, isReturn = false) => {
      const segment = item.segments[0];
      const flightSegment = segment.flightSegments[0];
      return {
        origin: { city: formatCity(item.originCity) },
        destination: { city: formatCity(item.destinationCity) },
        dateFormatted: StringUtils.capitalizeWords(
          DateUtils.formatToHumanDate(item.departureDate) ?? ''
        ),
        class: StringUtils.capitalizeWords(
          SearchParamsUtils.getFlightClassByCode(itinerary.params.flightClass)
        ),
        fee: upsell?.description ?? null,
        passengers: this.getPassengers(detailPricing),
        airlineName: flightSegment.marketingAirline.name,
        airlineImg: flightSegment.marketingAirline.imageUrl,
        startTime: DateUtils.formatDateStringToHoursFormat(segment.startDateTime),
        startAirport: formatAirport(item.originCity),
        endTime: DateUtils.formatDateStringToHoursFormat(segment.endDateTime),
        endAirport: formatAirport(item.destinationCity),
        flightDuration: segment.flightDuration.replace('.', 'h ') + 'm',
        flightNumber: flightSegment.flightNumber,
        includesHandLuggage: !!(
          segment.equipaje?.cabina?.piezas ||
          (isReturn ? upsell?.includeHandBagRet : upsell?.includeHandBagDep)
        ),
        includesHoldLuggage: !!(
          segment.equipaje?.piezas ||
          (isReturn ? upsell?.includesHoldBagRet : upsell?.includesHoldBagDep)
        ),
      };
    };

    const buildPricing = () => {
      const pricing = detailPricing!;
      return {
        adtQty: pricing.totalADT,
        adtDescr: `Adulto${pricing.totalADT > 1 ? 's' : ''}`,
        adtAmt: NumberUtils.formatNumberWithDecimals(pricing.baseFareADT * pricing.totalADT),
        cnnQty: pricing.totalCNN,
        cnnDescr: `Menor${pricing.totalCNN > 1 ? 'es' : ''}`,
        cnnAmt: NumberUtils.formatNumberWithDecimals(pricing.baseFareCNN * pricing.totalCNN),
        infQty: pricing.totalINF,
        infDescr: `Infante${pricing.totalINF > 1 ? 's' : ''}`,
        infAmt: NumberUtils.formatNumberWithDecimals(pricing.baseFareINF * pricing.totalINF),
        feesTaxesAmt: NumberUtils.formatNumberWithDecimals(
          pricing.feeNMV + pricing.feePTA + pricing.taxes
        ),
        total: NumberUtils.formatNumberWithDecimals(pricing.totalPay),
      };
    };

    const added_product: AddedProduct = {
      destination: departure.length === 1 ? formatCity(departure[0].destinationCity) : null,
      departures: departure.map((item) => buildDeparturesReturn(item)),
      hasReturn: !!returns,
      pricing: buildPricing(),
    };

    if (returns && returns.segments?.[0]?.flightSegments?.[0])
      added_product.return = buildDeparturesReturn(returns, true);

    return { url, added_product };
  }

  static redirectionUrlBuilder(itinerary: FlightItinerary, email: string = ''): string {
    const { params, transactionId, flight, segments } = itinerary;
    const {
      flightType,
      departureLocation,
      arrivalLocation,
      departureDate,
      arrivalDate,
      adults,
      children,
      infants,
      flightClass,
    } = params;
    return `${location.origin}/booking/itinerary/${transactionId}/${flight.id}/${segments.join('-')}/${flightType}/${departureLocation}/${arrivalLocation}/${departureDate}/${arrivalDate}/${adults}/${children}/${infants}/${flightClass}?email=${email}`;
  }

  private static getPassengers(pricing?: PricingDetail): string {
    if (!pricing) return '';

    const parts: string[] = [];

    if (pricing.totalADT > 0)
      parts.push(`${pricing.totalADT} Adulto${pricing.totalADT > 1 ? 's' : ''}`);

    if (pricing.totalCNN > 0)
      parts.push(`${pricing.totalCNN} Menor${pricing.totalCNN > 1 ? 'es' : ''}`);

    if (pricing.totalINF > 0)
      parts.push(`${pricing.totalINF} Infante${pricing.totalINF > 1 ? 's' : ''}`);

    return parts.join('\n');
  }
}
